/**
 * Utility functions for formatting aircraft make and model names
 */

/**
 * Capitalizes make names properly
 * Example: "EMBRAER" -> "Embraer"
 */
export const capitalizeMakeName = (name: string): string => {
  // For make names, just capitalize first letter of each word
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Formats model names properly, preserving numbers and alphanumeric suffixes
 * Examples:
 * - "PHENOM 300" -> "Phenom 300"
 * - "PHENOM 100EV" -> "Phenom 100EV"
 * - "300" -> "300"
 */
export const formatModelName = (name: string): string => {
  return name
    .split(' ')
    .map((word) => {
      // If the word is all numbers or numbers followed by letters (like 300, 100EV), keep it as is
      if (/^\d+[A-Z]*$/i.test(word)) {
        return word.toUpperCase()
      }
      // Otherwise, capitalize first letter and lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

/**
 * Formats a complete aircraft name (make + model)
 * Example: "EMBRAER PHENOM 300" -> "Embraer Phenom 300"
 */
export const formatAircraftName = (makeName: string, modelName: string): string => {
  return `${capitalizeMakeName(makeName)} ${formatModelName(modelName)}`
}

/**
 * Formats price values for display
 * - Values are stored as full numbers (e.g., 37000000)
 * - If the value is a number (or numeric string), formats it as millions with $ prefix
 * - Otherwise returns the text as-is
 * - Always shows 3 decimal places for consistency
 * Examples:
 * - "13000000.00" -> "$13.000M"
 * - "16995000.00" -> "$16.995M"
 * - "1975000.00" -> "$1.975M"
 * - "500000" -> "$0.500M"
 * - "Inquire" -> "Inquire"
 * - "Make Offer" -> "Make Offer"
 */
export const formatPrice = (value: string | null | undefined): string => {
  if (!value) return '-'

  // Try to parse as number
  const numValue = parseFloat(value)

  // Check if it's a valid number
  if (!isNaN(numValue)) {
    // Convert to millions without rounding
    const millions = numValue / 1000000

    // Always format with exactly 3 decimal places
    const formatted = millions.toFixed(3)

    return `$${formatted}M`
  }

  // Otherwise return the text as-is
  return value
}

/**
 * Formats a take price value for display with up to 3 decimal places (no trailing zeros)
 * Examples:
 * - "13000000" -> "$13M"
 * - "13500000" -> "$13.5M"
 * - "13550000" -> "$13.55M"
 * - "13555000" -> "$13.555M"
 * - "13555500" -> "$13.556M" (rounds to 3 decimals max)
 */
export const formatTakePrice = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '-'

  const strValue = typeof value === 'number' ? value.toString() : value

  // Try to parse as number
  const numValue = parseFloat(strValue)

  // Check if it's a valid number
  if (!isNaN(numValue)) {
    // Convert to millions
    const millions = numValue / 1000000

    // Format with up to 3 decimal places, removing trailing zeros
    // First round to 3 decimal places, then remove trailing zeros
    const rounded = Math.round(millions * 1000) / 1000
    const formatted = rounded.toString()

    return `$${formatted}M`
  }

  // Otherwise return the text as-is (or dash for empty)
  return strValue || '-'
}

/**
 * Formats asking price for Actual Ask and Adjusted Ask columns
 * - If no value, returns "Inquire" as default
 * - If value is "Inquire", preserves it as "Inquire"
 * - Otherwise formats as a normal price
 * Examples:
 * - null/undefined -> "Inquire"
 * - "Inquire" -> "Inquire"
 * - 13000000 -> "$13.000M"
 */
export const formatAskingPrice = (value: string | number | null | undefined): string => {
  if (!value) return 'Inquire'

  const strValue = typeof value === 'number' ? value.toString() : value

  // Check if it's "Inquire" (case insensitive) - preserve it
  if (strValue.toLowerCase() === 'inquire') {
    return 'Inquire'
  }

  return formatPrice(strValue)
}

/**
 * Formats a take price modifier for display
 * Handles legacy "+-" value by converting to "+/-"
 * Examples:
 * - formatModifier('+-') -> "+/-"
 * - formatModifier('+/-') -> "+/-"
 * - formatModifier('Motivated') -> "Motivated"
 */
export const formatModifier = (modifier: string | null | undefined): string | null => {
  if (!modifier) return null
  // Handle legacy "+-" value
  if (modifier === '+-') return '+/-'
  return modifier
}

/**
 * Formats a take price with its modifier
 * Examples:
 * - formatPriceWithModifier(13000000, '+') -> "$13.000M +"
 * - formatPriceWithModifier(16995000, 'Motivated') -> "$16.995M Motivated"
 * - formatPriceWithModifier(null, '+') -> "-"
 */
export const formatPriceWithModifier = (
  value: string | number | null | undefined,
  modifier: string | null | undefined
): string => {
  const priceStr = typeof value === 'number' ? value.toString() : value
  const formattedPrice = formatPrice(priceStr)

  if (formattedPrice === '-' || !modifier) {
    return formattedPrice
  }

  const displayModifier = formatModifier(modifier)
  return `${formattedPrice} ${displayModifier}`
}

/**
 * Formats a stored price value (full number) for display in text inputs
 * Converts from storage format (37000000) to display format ($37.000M)
 * Always shows 3 decimal places for consistency
 * Examples:
 * - 37000000 -> "$37.000M"
 * - 1234000 -> "$1.234M"
 * - 1500000 -> "$1.500M"
 * - null -> ""
 */
export const formatPriceForInput = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''

  // Convert to millions
  const millions = value / 1000000

  // Always format with exactly 3 decimal places
  const formatted = millions.toFixed(3)

  return `$${formatted}M`
}

/**
 * Formats a stored take price value (full number) for display in text inputs
 * Converts from storage format (37000000) to display format ($37M)
 * Shows up to 3 decimal places without trailing zeros
 * Examples:
 * - 37000000 -> "$37M"
 * - 1234000 -> "$1.234M"
 * - 1500000 -> "$1.5M"
 * - 2520000 -> "$2.52M"
 * - null -> ""
 */
export const formatTakePriceForInput = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''

  // Convert to millions
  const millions = value / 1000000

  // Round to 3 decimal places, then remove trailing zeros
  const rounded = Math.round(millions * 1000) / 1000
  const formatted = rounded.toString()

  return `$${formatted}M`
}

/**
 * Converts a stored price value (full number) to millions for editing
 * Examples:
 * - 37000000 -> 37
 * - 1234000 -> 1.234
 * - null -> null
 */
export const priceToMillions = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) return null
  return value / 1000000
}

/**
 * Parses user price input (in millions) to storage format (full number)
 * Handles various input formats:
 * - "37" -> 37000000
 * - "1.234" -> 1234000
 * - "$1.234M" -> 1234000
 * - "$37M" -> 37000000
 * Returns null for empty or invalid input
 */
export const parsePriceInput = (input: string): number | null => {
  if (!input || input.trim() === '') return null

  // Remove $ and M characters, and whitespace
  let cleaned = input.replace(/[$Mm\s]/g, '').trim()

  // Parse as number (user enters millions)
  const millions = parseFloat(cleaned)

  if (isNaN(millions)) return null

  // Convert to full number for storage
  return millions * 1000000
}

/**
 * Formats date values to "mm/dd/yy" format
 * Examples:
 * - "2023-12-25" -> "12/25/23"
 * - "2023-01-01T00:00:00Z" -> "01/01/23"
 * - null/undefined -> "-"
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-'

  try {
    const date = new Date(value)
    if (isNaN(date.getTime())) return value // Return original if invalid date

    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)

    return `${month}/${day}/${year}`
  } catch {
    return value // Return original value if parsing fails
  }
}

/**
 * Formats option values in abbreviated format
 * Examples:
 * - 225200 -> "$225K"
 * - 1500000 -> "$1.5M"
 * - 750 -> "$750"
 */
export const formatOptionValue = (value: number): string => {
  if (value >= 1000000) {
    // For millions, show one decimal place if needed
    const millions = value / 1000000
    return millions % 1 === 0 ? `$${millions}M` : `$${millions.toFixed(1)}M`
  } else if (value >= 1000) {
    // For thousands, round to nearest thousand
    const thousands = Math.round(value / 1000)
    return `$${thousands}K`
  } else {
    // For values less than 1000, show as is
    return `$${value}`
  }
}

/**
 * Formats relative value prices
 * - Under $1M: Shows as whole number with K suffix (e.g., $500K)
 * - $1M and above: Shows with decimal and M suffix (e.g., $1.5M)
 * Examples:
 * - 500000 -> "$500K"
 * - 750000 -> "$750K"
 * - 1000000 -> "$1M"
 * - 1500000 -> "$1.5M"
 * - 16995000 -> "$16.995M"
 */
export const formatRelativeValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A'

  const numValue = typeof value === 'number' ? value : parseFloat(value)

  if (isNaN(numValue)) return 'N/A'

  const absValue = Math.abs(numValue)
  const sign = numValue < 0 ? '-' : ''

  if (absValue >= 1000000) {
    // $1M and above: show with exactly 3 decimal places and M suffix
    const millions = absValue / 1000000
    const formatted = millions.toFixed(3)
    return `${sign}$${formatted}M`
  } else if (absValue >= 1000) {
    // Under $1M but at least $1K: show as whole number with K suffix
    const thousands = Math.round(absValue / 1000)
    return `${sign}$${thousands}K`
  } else {
    // Under $1K: show as whole number
    return `${sign}$${Math.round(absValue)}`
  }
}

/**
 * Parses a serial number into sortable components for natural sorting
 * Handles patterns like:
 * - "2000EX-102" -> { prefix: "2000EX", separator: "-", number: 102 }
 * - "123" -> { prefix: "", separator: "", number: 123 }
 * - "ABC-456-DEF" -> { prefix: "ABC", separator: "-", number: 456 }
 * - "N123AB" -> { prefix: "N", separator: "", number: 123, suffix: "AB" }
 */
interface SerialNumberParts {
  prefix: string
  separator: string
  number: number | null
  suffix: string
  original: string
}

const parseSerialNumber = (serial: string): SerialNumberParts => {
  if (!serial) {
    return { prefix: '', separator: '', number: null, suffix: '', original: serial }
  }

  // Try to match common patterns:
  // 1. Prefix + separator + number (e.g., "2000EX-102")
  // 2. Just a number (e.g., "123")
  // 3. Prefix + number + suffix (e.g., "N123AB")

  // Pattern 1: Prefix (letters/numbers) + separator + number (most common for problematic cases like 2000EX-102)
  const prefixSeparatorNumber = serial.match(/^([A-Za-z0-9]+)([^A-Za-z0-9]+)(\d+)(.*)$/)
  if (prefixSeparatorNumber) {
    const [, prefix, separator, numberStr, suffix] = prefixSeparatorNumber
    return {
      prefix: prefix,
      separator: separator,
      number: parseInt(numberStr, 10),
      suffix: suffix,
      original: serial
    }
  }

  // Pattern 2: Just a number
  const justNumber = serial.match(/^\d+$/)
  if (justNumber) {
    return {
      prefix: '',
      separator: '',
      number: parseInt(serial, 10),
      suffix: '',
      original: serial
    }
  }

  // Pattern 3: Find the first number in the string (for cases like N123AB)
  const firstNumber = serial.match(/^([^\d]*)(\d+)(.*)$/)
  if (firstNumber) {
    const [, prefix, numberStr, suffix] = firstNumber
    return {
      prefix: prefix,
      separator: '',
      number: parseInt(numberStr, 10),
      suffix: suffix,
      original: serial
    }
  }

  // Fallback: no numbers found, treat as pure string
  return {
    prefix: serial,
    separator: '',
    number: null,
    suffix: '',
    original: serial
  }
}

/**
 * Compares two serial numbers using natural sorting
 * Handles alphanumeric patterns intelligently
 * Examples:
 * - "2000EX-11" comes before "2000EX-102"
 * - "ABC-1" comes before "ABC-10"
 * - "N123AB" comes before "N124AB"
 */
export const compareSerialNumbers = (serialA: string, serialB: string): number => {
  const partsA = parseSerialNumber(serialA)
  const partsB = parseSerialNumber(serialB)

  // First compare prefixes
  const prefixComparison = partsA.prefix.toLowerCase().localeCompare(partsB.prefix.toLowerCase())
  if (prefixComparison !== 0) {
    return prefixComparison
  }

  // Then compare separators
  const separatorComparison = partsA.separator.localeCompare(partsB.separator)
  if (separatorComparison !== 0) {
    return separatorComparison
  }

  // Then compare numbers numerically
  if (partsA.number !== null && partsB.number !== null) {
    const numberComparison = partsA.number - partsB.number
    if (numberComparison !== 0) {
      return numberComparison
    }
  } else if (partsA.number !== null && partsB.number === null) {
    return -1 // Numbers come before non-numbers
  } else if (partsA.number === null && partsB.number !== null) {
    return 1 // Non-numbers come after numbers
  }

  // Finally compare suffixes
  const suffixComparison = partsA.suffix.toLowerCase().localeCompare(partsB.suffix.toLowerCase())
  if (suffixComparison !== 0) {
    return suffixComparison
  }

  // If all parts are equal, fall back to original string comparison
  return partsA.original.toLowerCase().localeCompare(partsB.original.toLowerCase())
}